import { useState, type ReactNode } from 'react'
import type { StrategyMode } from '../../types/strategy'
import type { AllocationSimulationResponse } from '../../types/simulation'
import { Spinner } from '../common/Spinner'
import { WIZARD_STRATEGIES } from './types'

interface Props {
  mode: StrategyMode
  result: AllocationSimulationResponse | null
  loading: boolean
  /** The amount field (owned by the wizard, which validates it). */
  amountSlot: ReactNode
  /** False until the amount is valid: the next step needs a plan for a real amount. */
  canContinue: boolean
  onConfirm: (mode: StrategyMode) => void
}

function RiskBar({ level }: { level: 1 | 2 | 3 }) {
  return (
    <div className="flex gap-1 items-center">
      {[1, 2, 3].map(i => (
        <div
          key={i}
          className={`h-1.5 flex-1 rounded-full ${
            i <= level
              ? level === 1 ? 'bg-emerald-400' : level === 2 ? 'bg-accent-blue' : 'bg-accent-purple'
              : 'bg-white/10'
          }`}
        />
      ))}
    </div>
  )
}

export function StrategyStep({ mode, result, loading, amountSlot, canContinue, onConfirm }: Props) {
  const [localMode, setLocalMode] = useState<StrategyMode>(mode)

  const selectedConfig = WIZARD_STRATEGIES.find(s => s.mode === localMode)!
  const liveApy = result && localMode === mode
    ? (result.allocation.theoreticalNetApyPct * 100).toFixed(1) + '%'
    : null

  return (
    <div className="space-y-5">
      {/* Hero */}
      <div className="text-center pt-2">
        <h1 className="text-xl font-bold text-white mb-1">Start earning on your SOL</h1>
        <p className="text-sm text-gray-400">Pick a strategy, set your amount, and follow the steps. No DeFi experience needed.</p>
      </div>

      {/* Amount */}
      {amountSlot}

      {/* Strategy cards */}
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Choose your strategy</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {WIZARD_STRATEGIES.map(cfg => {
            const selected = localMode === cfg.mode
            return (
              <button
                key={cfg.mode}
                type="button"
                onClick={() => setLocalMode(cfg.mode)}
                className={`relative rounded-xl border p-4 text-left transition-all duration-200 ${
                  selected
                    ? `${cfg.borderClass} ${cfg.bgClass} ring-1 ring-inset ${cfg.borderClass}`
                    : 'border-white/5 bg-surface-card hover:border-white/15 hover:bg-white/[0.03]'
                }`}
              >
                {selected && (
                  <span className="absolute top-3 right-3 w-4 h-4 rounded-full bg-accent-purple flex items-center justify-center">
                    <svg viewBox="0 0 10 10" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" className="w-2.5 h-2.5">
                      <polyline points="1.5 5 4 7.5 8.5 2" />
                    </svg>
                  </span>
                )}

                {/* APY */}
                <div className={`text-2xl font-bold mb-0.5 ${selected ? cfg.accentClass : 'text-white'}`}>
                  {selected && liveApy ? liveApy : cfg.apyRange}
                </div>
                <div className="text-[10px] text-gray-500 mb-3">
                  {selected && liveApy ? 'live APY estimate' : 'estimated APY'}
                </div>

                {/* Name + tagline */}
                <div className={`text-sm font-bold mb-0.5 ${selected ? cfg.accentClass : 'text-gray-200'}`}>
                  {cfg.name}
                </div>
                <div className="text-[11px] text-gray-400 mb-3 leading-relaxed">{cfg.desc}</div>

                {/* Risk bar */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-500">Risk</span>
                    <span className={`text-[10px] font-medium ${cfg.accentClass}`}>{cfg.riskLabel}</span>
                  </div>
                  <RiskBar level={cfg.riskLevel} />
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Selected strategy summary */}
      {selectedConfig && (
        <div className={`rounded-xl border ${selectedConfig.borderClass} ${selectedConfig.bgClass} p-4`}>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2">What you'll do</div>
          <p className="text-sm text-gray-200 leading-relaxed">{selectedConfig.tagline}</p>
        </div>
      )}

      {/* CTA */}
      <button
        type="button"
        disabled={loading || !canContinue}
        onClick={() => onConfirm(localMode)}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-accent-purple text-white font-semibold text-sm hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
      >
        {loading ? (
          <><Spinner size="sm" /> Calculating strategy…</>
        ) : (
          <>
            Next: Get your SOL
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4">
              <polyline points="6 3 11 8 6 13" />
            </svg>
          </>
        )}
      </button>
    </div>
  )
}
